import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Modal, Form, Input } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { TemplateService } from 'utility/services/template'
import { AuthService } from 'utility/services/auth'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { AppEditButton, AppRemoveButtonPopup, LoaderBar } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { X } from 'phosphor-react'
import { GetCompanyTemplateList, GetCustomerList } from 'hooks'
import styled from 'styled-components'

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 720px) {
        flex-direction: column;
        align-items: stretch;

        .ui.button {
            width: 100%;
        }
    }
`

const Heading = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #1c1917;
`

const Frame = styled.div`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    overflow-x: auto;
`

const Grid = styled.table`
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    table-layout: fixed;

    th {
        text-align: left;
        padding: 12px 14px;
        background: #fafaf9;
        color: #78716c;
        font-size: 12px;
        font-weight: 600;
        border-bottom: 1px solid #e7e5e4;
        white-space: nowrap;
    }

    td {
        padding: 10px 14px;
        border-bottom: 1px solid #f5f5f4;
        color: #1c1917;
        font-size: 14px;
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    tbody tr:hover td {
        background: #fafaf9;
    }

    th:last-child,
    td:last-child {
        width: 112px;
        text-align: right;
        white-space: nowrap;
    }
`

const Clip = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;

    .ui.button {
        margin: 0 !important;
        padding: 0.55em !important;
        min-width: 32px !important;
        width: 32px;
        height: 32px;
    }
`

const Card = styled.article`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    padding: 14px;
    margin-bottom: 10px;
`

const CardTop = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
`

const CardName = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: #1c1917;
    line-height: 1.3;
`

const CardMeta = styled.div`
    margin-top: 4px;
    color: #78716c;
    font-size: 13px;
`

const Empty = styled.div`
    padding: 36px 16px;
    text-align: center;
    color: #78716c;
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
`

const shown = value => {
    const raw = value == null ? '' : String(value).trim()
    return raw || '—'
}

const customerNameOf = (item, customerData) => {
    const customer = (customerData || []).find(entry => entry.unique_id === item.CustomerId)
    return shown(customer?.customer_name || item.CustomerName)
}

const combineTemplateContainerStyle = {
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    minHeight: '42px',
    padding: '6px 8px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '8px',
    backgroundColor: '#fff',
}

const combineTemplateChipStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#f4f6fb',
    border: '1px solid #d0d7ff',
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'grab',
    userSelect: 'none',
}

const orderBadgeStyle = {
    fontSize: '0.75rem',
    background: '#e1e7ff',
    color: '#324a8a',
    borderRadius: '3px',
    padding: '2px 6px',
}

const removeIconStyle = {
    color: '#9a1b1f',
}

export const TemplateGroupScreen = () => {
    const [groups, setGroups] = useState([])
    const [loading, setLoading] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [groupName, setGroupName] = useState('')
    const [description, setDescription] = useState('')
    const [customerId, setCustomerId] = useState(null)
    const [error, setError] = useState('')
    const [selectedTemplates, setSelectedTemplates] = useState([])
    const [draggingIndex, setDraggingIndex] = useState(null)
    const [dropIndex, setDropIndex] = useState(null)
    const [pickedTemplate, setPickedTemplate] = useState(null)
    const [editGroup, setEditGroup] = useState(null)
    const user = AuthService.getUser()
    const companyId = user?.Companies?.[0]?.CompanyId || 1
    const { options: templateOptions, data: templateDataList } = GetCompanyTemplateList()
    const { options: customerOptions, data: customerData } = GetCustomerList()

    const filteredTemplateOptions = useMemo(
        () =>
            (templateOptions || []).filter(
                option => !selectedTemplates.some(item => item.value === option.value),
            ),
        [templateOptions, selectedTemplates],
    )

    useEffect(() => {
        let isMounted = true
        const fetchGroupsSafe = async () => {
            setLoading(true)
            try {
                const res = await TemplateService.getSequenceGroupsByCompany(companyId, true)
                const groupsList = res?.Records || res || []
                // Normalize group names - handle both GroupName and TemplateGroupName
                const normalizedGroups = groupsList.map(group => ({
                    ...group,
                    GroupName: group.GroupName || group.TemplateGroupName || '',
                }))
                if (isMounted) setGroups(normalizedGroups)
            } catch (e) {
                if (isMounted) setError('Failed to load template groups')
            } finally {
                if (isMounted) setLoading(false)
            }
        }
        fetchGroupsSafe()
        return () => {
            isMounted = false
        }
    }, [companyId])

    const fetchGroups = async () => {
        setLoading(true)
        try {
            const res = await TemplateService.getSequenceGroupsByCompany(companyId, true)
            const groupsList = res?.Records || res || []
            // Normalize group names - handle both GroupName and TemplateGroupName
            const normalizedGroups = groupsList.map(group => ({
                ...group,
                GroupName: group.GroupName || group.TemplateGroupName || '',
            }))
            setGroups(normalizedGroups)
        } catch (e) {
            setError('Failed to load template groups')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!pickedTemplate || pickedTemplate === '' || pickedTemplate === null) {
            return
        }
        const option = templateOptions.find(opt => opt.value === pickedTemplate)
        if (option) {
            // Find the full template data to get TemplateUniqueId if available
            const fullTemplateData = templateDataList?.find(
                t => t.TemplateId === option.value,
            )
            setSelectedTemplates(prev => {
                if (prev.some(item => item.value === option.value)) {
                    setPickedTemplate(null)
                    return prev
                }
                const updated = [
                    ...prev,
                    {
                        ...option,
                        SequenceOrder: prev.length + 1,
                        TemplateId: option.value, // TemplateId from dropdown value
                        TemplateUniqueId: fullTemplateData?.TemplateUniqueId || option.value, // TemplateUniqueId if available
                        UniqueId: option.UniqueId || fullTemplateData?.UniqueId, // UniqueId from CompanyTemplate API
                    },
                ]
                setPickedTemplate(null)
                return updated
            })
            setError('')
        } else {
            setPickedTemplate(null)
        }
    }, [pickedTemplate, templateOptions])

    useEffect(() => {
        if (!templateOptions?.length) {
            return
        }
        setSelectedTemplates(prev =>
            prev.map(item => {
                const option = templateOptions.find(opt => opt.value === item.value)
                if (!option) {
                    return item
                }
                return { ...item, text: option.text }
            }),
        )
    }, [templateOptions])

    const openModal = async group => {
        if (group) {
            setLoading(true)
            try {
                const res = await TemplateService.getSequenceGroupById(group.SequenceGroupId)
                setEditGroup(group)
                setGroupName(res?.GroupName || group.GroupName)
                setDescription(res?.Description || '')
                setCustomerId(res?.CustomerId || group.CustomerId || null)

                // Fetch sequence details for this group
                const detailsRes = await TemplateService.getSequenceDetailsByGroupId(
                    group.SequenceGroupId,
                    true,
                )
                const details = detailsRes?.Records || detailsRes || []

                // Map sequence details to selected templates with order
                // TemplateUniqueId from GroupDetails API (GUID) should match value from templateOptions (which is TemplateId GUID from CompanyTemplate API)
                if (!templateOptions || templateOptions.length === 0) {
                    console.warn('Template options not loaded yet')
                    setError('Template options are not loaded. Please try again.')
                    setLoading(false)
                    return
                }

                const templatesWithOrder = details
                    .sort((a, b) => a.SequenceOrder - b.SequenceOrder)
                    .map(detail => {
                        // Match by TemplateUniqueId (GUID) - the value field in options is the TemplateId GUID from CompanyTemplate API
                        // Use case-insensitive comparison to handle any GUID format differences
                        const templateOption = templateOptions.find(
                            opt => {
                                const optValue = String(opt.value || '').toLowerCase().trim()
                                const detailValue = String(detail.TemplateUniqueId || '').toLowerCase().trim()
                                return optValue === detailValue && optValue !== ''
                            },
                        )

                        if (templateOption) {
                            // Find the full template data to get UniqueId if available
                            const fullTemplateData = templateDataList?.find(
                                t => {
                                    const tTemplateId = String(t.TemplateId || '').toLowerCase().trim()
                                    const tTemplateUniqueId = String(t.TemplateUniqueId || '').toLowerCase().trim()
                                    const detailValue = String(detail.TemplateUniqueId || '').toLowerCase().trim()
                                    const optValue = String(templateOption.value || '').toLowerCase().trim()
                                    return tTemplateId === optValue || tTemplateUniqueId === detailValue
                                },
                            )
                            return {
                                ...templateOption,
                                SequenceOrder: detail.SequenceOrder,
                                SequenceDetailId: detail.SequenceDetailId,
                                IsMandatory: detail.IsMandatory,
                                TemplateUniqueId: detail.TemplateUniqueId, // Use TemplateUniqueId from GroupDetails API
                                TemplateId: templateOption.value, // value is the TemplateId GUID from CompanyTemplate API
                                UniqueId: templateOption.UniqueId || fullTemplateData?.UniqueId, // UniqueId from CompanyTemplate API
                            }
                        }
                        console.warn('Template not found for detail:', {
                            detail,
                            searchedTemplateUniqueId: detail.TemplateUniqueId,
                            searchedTemplateUniqueIdLower: String(detail.TemplateUniqueId || '').toLowerCase().trim(),
                            availableOptionValues: templateOptions.map(opt => ({
                                value: opt.value,
                                valueLower: String(opt.value || '').toLowerCase().trim(),
                                text: opt.text,
                            })).slice(0, 10), // Show first 10 for debugging
                        })
                        return null
                    })
                    .filter(Boolean)

                setSelectedTemplates(templatesWithOrder)
                setModalOpen(true)
            } catch (e) {
                setError('Failed to load group details')
                setLoading(false)
            } finally {
                setLoading(false)
            }
        } else {
            setEditGroup(null)
            setGroupName('')
            setDescription('')
            setCustomerId(null)
            setSelectedTemplates([])
            setPickedTemplate(null)
            setModalOpen(true)
        }
        setError('')
    }

    const closeModal = () => {
        setModalOpen(false)
        setGroupName('')
        setDescription('')
        setCustomerId(null)
        setSelectedTemplates([])
        setPickedTemplate(null)
        setError('')
    }

    const onTemplateRemove = useCallback(
        id => {
            const updated = (selectedTemplates || []).filter(
                item => item.value !== id,
            )
            setSelectedTemplates(
                updated.map((item, index) => ({
                    ...item,
                    SequenceOrder: index + 1,
                })),
            )
        },
        [selectedTemplates],
    )

    const applyOrder = useCallback(list => {
        setSelectedTemplates(
            list.map((item, index) => ({
                ...item,
                SequenceOrder: index + 1,
            })),
        )
    }, [])

    const handleDragStart = (event, index) => {
        if (event?.dataTransfer) {
            event.dataTransfer.setData('index', String(index))
            event.dataTransfer.effectAllowed = 'move'
        }
        setDraggingIndex(index)
        setDropIndex(index)
    }

    const handleDragOver = event => {
        event.preventDefault()
    }

    const handleDrop = (index, event) => {
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }
        const targetIndex = dropIndex !== null ? dropIndex : index
        if (
            draggingIndex === null ||
            targetIndex === null ||
            draggingIndex === targetIndex
        ) {
            setDraggingIndex(null)
            setDropIndex(null)
            return
        }
        const updated = Array.from(selectedTemplates)
        const [moved] = updated.splice(draggingIndex, 1)
        let insertIndex = targetIndex
        if (insertIndex < 0) {
            insertIndex = 0
        }
        if (insertIndex > updated.length) {
            insertIndex = updated.length
        }
        updated.splice(insertIndex, 0, moved)
        applyOrder(updated)
        setDraggingIndex(null)
        setDropIndex(null)
    }

    const handleContainerDrop = event => {
        if (event) {
            event.preventDefault()
        }
        if (event.target !== event.currentTarget) {
            return
        }
        if (draggingIndex !== null) {
            const target = dropIndex !== null ? dropIndex : selectedTemplates.length
            handleDrop(target, event)
        }
    }

    const handleDragEnterContainer = event => {
        if (event) {
            event.preventDefault()
        }
        if (event.target === event.currentTarget) {
            setDropIndex(selectedTemplates.length)
        }
    }

    const handleItemDragEnter = index => {
        if (draggingIndex === null) {
            return
        }
        const nextIndex = draggingIndex < index ? index + 1 : index
        setDropIndex(nextIndex)
    }

    const handleDragEnd = () => {
        setDraggingIndex(null)
        setDropIndex(null)
    }

    const handleDeleteGroup = async group => {
        setLoading(true)
        setError('')
        try {
            await TemplateService.deleteSequenceGroup(group.SequenceGroupId)
            fetchGroups()
        } catch (e) {
            setError(e?.error?.message || e?.message || 'Failed to delete group')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async () => {
        if (!groupName.trim()) {
            setError('Please enter a group name')
            return
        }
        if (!customerId) {
            setError('Please select a customer')
            return
        }
        if (selectedTemplates.length === 0) {
            setError('Please select at least one template')
            return
        }

        // Check for duplicate group name with same customer
        const duplicateGroup = groups.find(
            g => g.GroupName?.toLowerCase().trim() === groupName.toLowerCase().trim() &&
            g.CustomerId === customerId &&
            (!editGroup || g.SequenceGroupId !== editGroup.SequenceGroupId),
        )

        if (duplicateGroup) {
            const customerName = customerOptions.find(c => c.value === customerId)?.text || 'selected customer'
            setError(`A template group with the name "${groupName}" already exists for ${customerName}. Please use a different name or customer.`)
            return
        }

        setLoading(true)
        setError('')
        try {
            let sequenceGroupId

            // Extract UniqueIds from selected templates and format as semicolon-separated string
            const uniqueIds = selectedTemplates
                .map(template => {
                    // Get UniqueId from template object or find from templateDataList
                    if (template.UniqueId) {
                        return template.UniqueId
                    }
                    const fullTemplateData = templateDataList?.find(
                        t => t.TemplateId === template.value || t.TemplateId === template.TemplateId,
                    )
                    return fullTemplateData?.UniqueId
                })
                .filter(Boolean) // Remove any undefined/null values

            // Format as semicolon-separated string: "46044;46045"
            const templateIdsString = uniqueIds.join(';')

            // Get customer name from customer data
            const selectedCustomer = customerData.find(c => c.unique_id === customerId)
            const customerName = selectedCustomer?.customer_name || ''

            // Save or update sequence group
            // Note: customerId is the unique_id from the customer API response
            if (editGroup) {
                await TemplateService.saveSequenceGroup({
                    SequenceGroupId: editGroup.SequenceGroupId,
                    GroupName: groupName,
                    CompanyId: companyId,
                    CustomerId: customerId, // unique_id from customer API
                    CustomerName: customerName, // customer_name from customer API
                    Description: description,
                    IsActive: true,
                    TemplateIds: templateIdsString,
                })
                sequenceGroupId = editGroup.SequenceGroupId
            } else {
                const groupRes = await TemplateService.saveSequenceGroup({
                    SequenceGroupId: 0,
                    GroupName: groupName,
                    CompanyId: companyId,
                    CustomerId: customerId, // unique_id from customer API
                    CustomerName: customerName, // customer_name from customer API
                    Description: description,
                    IsActive: true,
                    TemplateIds: templateIdsString,
                })
                sequenceGroupId = groupRes?.SequenceGroupId || groupRes?.Records?.[0]?.SequenceGroupId
            }

            if (!sequenceGroupId) {
                throw new Error('Failed to create or update group')
            }

            // Get existing sequence details for this group
            const existingDetails = editGroup
                ? await TemplateService.getSequenceDetailsByGroupId(sequenceGroupId, false)
                : { Records: [] }
            const existingDetailsList = existingDetails?.Records || existingDetails || []

            // Soft delete removed templates
            const currentTemplateIds = selectedTemplates.map(t => t.value)
            const removedDetails = existingDetailsList.filter(
                detail => !currentTemplateIds.includes(detail.TemplateUniqueId),
            )
            const deletePromises = removedDetails.map(detail =>
                TemplateService.deleteSequenceDetail(detail.SequenceDetailId),
            )
            await Promise.all(deletePromises)

            // Save or update sequence details for each selected template
            const savePromises = selectedTemplates.map((template, index) => {
                // Use TemplateUniqueId if available, otherwise use value (GUID)
                const templateUniqueId = template.TemplateUniqueId || template.value

                // Get UniqueId from template object or find from templateDataList
                // UniqueId is the integer TemplateId (same approach as group creation)
                let integerTemplateId = template.UniqueId
                if (!integerTemplateId) {
                    // Handle both array and object with Items property
                    const templateList = Array.isArray(templateDataList)
                        ? templateDataList
                        : (templateDataList?.Items || templateDataList?.items || [])

                    const fullTemplateData = templateList.find(
                        t => {
                            const tTemplateId = String(t.TemplateId || '').trim()
                            const tTemplateUniqueId = String(t.TemplateUniqueId || '').toLowerCase().trim()
                            const templateValue = String(template.value || '').trim()
                            const templateUniqueIdLower = String(templateUniqueId || '').toLowerCase().trim()

                            // Match by TemplateUniqueId (GUID) - most reliable
                            if (tTemplateUniqueId && templateUniqueIdLower && tTemplateUniqueId === templateUniqueIdLower) {
                                return true
                            }
                            // Match by TemplateId
                            if (tTemplateId && templateValue && tTemplateId === templateValue) {
                                return true
                            }
                            // Match TemplateUniqueId with template.value (in case value is GUID)
                            if (tTemplateUniqueId && templateValue && tTemplateUniqueId === templateValue.toLowerCase().trim()) {
                                return true
                            }
                            return false
                        },
                    )
                    integerTemplateId = fullTemplateData?.UniqueId
                }

                // Ensure we have an integer TemplateId
                if (!integerTemplateId) {
                    console.error('UniqueId (integer TemplateId) not found for template:', {
                        template,
                        templateUniqueId,
                        templateValue: template.value,
                    })
                    throw new Error(`Invalid TemplateId for template: ${template.text}. TemplateId must be an integer, but UniqueId is missing.`)
                }

                // Parse to ensure it's an integer
                const parsedTemplateId = parseInt(integerTemplateId, 10)
                if (Number.isNaN(parsedTemplateId)) {
                    console.error('Invalid TemplateId found (must be integer):', {
                        template,
                        integerTemplateId,
                        parsedTemplateId,
                    })
                    throw new Error(`Invalid TemplateId for template: ${template.text}. TemplateId must be an integer, but got: ${integerTemplateId}`)
                }

                const existingDetail = existingDetailsList.find(
                    detail =>
                        detail.TemplateUniqueId === templateUniqueId ||
                        detail.TemplateId === templateUniqueId ||
                        detail.TemplateId === integerTemplateId,
                )

                const payload = {
                    SequenceDetailId: existingDetail?.SequenceDetailId || 0,
                    SequenceGroupId: sequenceGroupId,
                    TemplateUniqueId: templateUniqueId,
                    TemplateId: parsedTemplateId, // Use the parsed integer TemplateId
                    SequenceOrder: template.SequenceOrder || index + 1,
                    IsMandatory:
                        template.IsMandatory !== undefined ? template.IsMandatory : true,
                    IsActive: true,
                }

                // Debug: Log payload to verify TemplateId is included as integer
                console.log('Sequence Detail Payload:', payload)

                return TemplateService.saveSequenceDetail(payload)
            })
            await Promise.all(savePromises)

            closeModal()
            fetchGroups()
        } catch (e) {
            setError(e?.error?.message || e?.message || 'Failed to create or update group')
        } finally {
            setLoading(false)
        }
    }

    const rows = Array.isArray(groups) ? groups : []

    return (
        <div>
            {loading && <LoaderBar content="Loading" />}
            <Toolbar>
                <Heading>Template Groups</Heading>
                <AppPrimaryButton primary onClick={() => openModal(null)}>
                    Create Group
                </AppPrimaryButton>
            </Toolbar>
            {!loading && rows.length === 0 && <Empty>No template groups yet.</Empty>}
            {rows.length > 0 && (
                <>
                    <DesktopMode>
                        <Frame>
                            <Grid>
                                <thead>
                                    <tr>
                                        <th>Group name</th>
                                        <th>Customer</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(item => (
                                        <tr key={item.SequenceGroupId}>
                                            <td><Clip>{shown(item.GroupName)}</Clip></td>
                                            <td><Clip>{customerNameOf(item, customerData)}</Clip></td>
                                            <td>
                                                <Actions>
                                                    <AppEditButton onClick={() => openModal(item)} />
                                                    <AppRemoveButtonPopup onConfirm={() => handleDeleteGroup(item)} />
                                                </Actions>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Grid>
                        </Frame>
                    </DesktopMode>
                    <MobileMode>
                        {rows.map(item => (
                            <Card key={item.SequenceGroupId}>
                                <CardTop>
                                    <div>
                                        <CardName>{shown(item.GroupName)}</CardName>
                                        <CardMeta>{customerNameOf(item, customerData)}</CardMeta>
                                    </div>
                                    <Actions>
                                        <AppEditButton onClick={() => openModal(item)} />
                                        <AppRemoveButtonPopup onConfirm={() => handleDeleteGroup(item)} />
                                    </Actions>
                                </CardTop>
                            </Card>
                        ))}
                    </MobileMode>
                </>
            )}
            <Modal open={modalOpen} onClose={closeModal} size="small">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 1.5rem 0.5rem 1.5rem' }}>
                    <div style={{ fontSize: 22, fontWeight: 500 }}>
                        {editGroup == null ? 'Create Group' : 'Edit Group'}
                    </div>
                    <button
                        type="button"
                        onClick={closeModal}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, lineHeight: 1 }}
                        aria-label="Close"
                    >
                        <X size={28} />
                    </button>
                </div>
                <Modal.Content>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Group Name <span style={{ color: 'red' }}>*</span></label>
                                <Input value={groupName} onChange={e => setGroupName(e.target.value)} />
                            </Form.Field>
                            <Form.Field>
                                <label>Customer <span style={{ color: 'red' }}>*</span></label>
                                <Dropdown
                                    placeholder="Select Customer"
                                    search
                                    selection
                                    clearable
                                    options={customerOptions}
                                    value={customerId || null}
                                    onChange={(e, { value }) => setCustomerId(value || null)}
                                />
                            </Form.Field>
                        </Form.Group>
                        <Form.Field>
                            <label>Description</label>
                            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
                        </Form.Field>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Template</label>
                                <Dropdown
                                    placeholder="Select Template"
                                    search
                                    selection
                                    clearable
                                    selectOnBlur={false}
                                    selectOnNavigation={false}
                                    options={filteredTemplateOptions}
                                    value={pickedTemplate || null}
                                    onChange={(e, { value }) => {
                                        // Only set if value is explicitly provided and not empty
                                        // This ensures we only select when user explicitly clicks an option
                                        if (value && value !== '' && value !== null && value !== undefined) {
                                            setPickedTemplate(value)
                                        } else {
                                            setPickedTemplate(null)
                                        }
                                    }}
                                    onClose={() => {
                                        // Reset to null if no selection was made
                                        // This prevents auto-selection when closing dropdown
                                        if (!pickedTemplate) {
                                            setPickedTemplate(null)
                                        }
                                    }}
                                    closeOnChange={false}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Combine Template</label>
                                <div
                                    style={combineTemplateContainerStyle}
                                    onDragOver={handleDragOver}
                                    onDrop={handleContainerDrop}
                                    onDragEnter={handleDragEnterContainer}
                                >
                                    {selectedTemplates.length === 0 && (
                                        <span style={{ color: '#999' }}>
                                            No templates selected
                                        </span>
                                    )}
                                    {selectedTemplates.map((item, index) => (
                                        <div
                                            key={item.value}
                                            style={combineTemplateChipStyle}
                                            draggable
                                            onDragStart={event => handleDragStart(event, index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={handleDragOver}
                                            onDrop={event => handleDrop(index, event)}
                                            onDragEnter={() => handleItemDragEnter(index)}
                                        >
                                            <span>{item.text}</span>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginLeft: '8px',
                                                    gap: '8px',
                                                }}
                                            >
                                                <span style={orderBadgeStyle}>{item.SequenceOrder}</span>
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ ...removeIconStyle, cursor: 'pointer', fontWeight: 600 }}
                                                    onClick={() => onTemplateRemove(item.value)}
                                                    onKeyDown={event => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            onTemplateRemove(item.value)
                                                        }
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </Form.Field>
                        </Form.Group>
                        {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
                    </Form>
                </Modal.Content>
                <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <AppSecondaryButton onClick={closeModal}>Cancel</AppSecondaryButton>
                    <AppPrimaryButton primary onClick={handleSubmit} disabled={loading}>
                        {(() => {
                            if (loading) {
                                return 'Saving...'
                            }
                            if (editGroup == null) {
                                return 'Create'
                            }
                            return 'Update'
                        })()}
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </div>
    )
}
